import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, BookOpen } from 'lucide-react';
import apiClient from '../../config/axios';
import { track } from '../../utils/analytics';
import FavoriteButton from '../../components/FavoriteButton';
import CopyEntryButtons from '../../components/generic/CopyEntryButtons';
import PrintButton from '../../components/generic/PrintButton';
import ReaderModeButton, { useReaderMode } from '../../components/generic/ReaderModeButton';
import ErrorBoundary from '../../components/ErrorBoundary';
import MonsterTemplates from '../../components/MonsterTemplates';
import Breadcrumbs from '../../components/generic/Breadcrumbs';
import DetailSection, { CARD_ONLY_STYLES } from '../../components/generic/DetailSection';
import StatHeading from '../../components/generic/StatHeading';
import { ConditionTermsProvider } from '../../context/ConditionTermsContext';
import { loadConditions } from '../../config/conditionsLoader';
import SimilarRail from '../../components/generic/SimilarRail';
import QualifyPanel from '../../components/generic/QualifyPanel';
import RollTablePanel from '../../components/generic/RollTablePanel';
import {
  resolvedPrinting, PrintingsPicker, AlsoAppearsIn, IncompleteNotice, RulebookLink,
} from '../../components/generic/PrintingsPicker';
import { badgeItems } from '../../components/generic/AttrBadge';
import composeLine from '../../components/generic/composeLine';
import { getFamily } from '../../config/familyRegistry';
import { getActiveSystem } from '../../config/system';
import { attemptCrossSystemRedirect, ensureSystemInUrl } from '../../utils/crossSystemLink';
import useRecordView from '../../hooks/useRecordView';
import { loadFailureText } from '../../utils/loadFailure';
import EntryNotePanel from '../../components/EntryNotePanel';

/**
 * The one detail page (05 §1.2), rendered from the Family's derived sections.
 *
 * The four universal detail behaviors of 02b Part 2 are preserved exactly, and
 * they are the reason this component exists rather than ten near-copies:
 *
 *  • `?slug=` addressing, with `?rulebook=` riding through as the printing pin;
 *  • `backTo = location.state?.from || '/<segment>'` — the nav contract's fourth
 *    point — reused on the error card as well as the header;
 *  • a cancellation guard, so a rapid slug change cannot render entity A under
 *    URL B;
 *  • on a 404, `attemptCrossSystemRedirect` (#92), and on success
 *    `ensureSystemInUrl` so a copied link is self-describing.
 *
 * `ErrorBoundary` wraps the body (02b Part 5 #6 — built but never mounted).
 *
 * TWO VISUAL LANGUAGES, ONE COMPONENT (v2.4, 02b Part 5 item 2). A Family's
 * `detail_style` decides whether the page is a `.stat-block` — a banner with
 * the name, the header badge cluster and a composed subtitle, over a body of
 * `.stat-block-row` pairs — or a stack of `.card` sections under an
 * `h1.page-title`. It is a different MARKUP, not a skin: the pinned content
 * specs assert those class names by hand, and 02c §5.4 says not to genericize
 * them. Prose sections render as cards under BOTH, because that is what the
 * shipped stat-block pages do with a description.
 *
 * THE PRINTINGS SURFACE landed at R12 with Classes, which is the one shipped
 * detail that had a picker (02-parity-inventory row 6 records giving it to the
 * other nine as a parity GAIN). It is three platform pieces, none of them
 * authored — the picker, "Also Appears In", and the completeness marker —
 * and they live in `components/generic/PrintingsPicker.js`.
 */
/**
 * Open on the workbench (qa-runs 373): the entry as a schematic of blocks, its fields and its mechanics, read-only
 * there with Copy to edit.
 */
function WorkbenchLink({ segment, slug }) {
  if (!slug) return null;
  const to = `/authoring/workbench?system=${encodeURIComponent(getActiveSystem())}&entry=${encodeURIComponent(`${segment}/${slug}`)}`;
  return (
    <Link to={to} className="btn btn-secondary text-xs px-2 py-1 no-print" title="See how this entry is built, block by block, and copy any part of it" data-testid="open-on-workbench">
      Open on the workbench
    </Link>
  );
}
WorkbenchLink.propTypes = { segment: PropTypes.string.isRequired, slug: PropTypes.string };

export default function GenericDetail({ segment }) {
  const family = getFamily(segment, getActiveSystem());
  const [searchParams, setSearchParams] = useSearchParams();
  const slug = searchParams.get('slug');
  const rulebook = searchParams.get('rulebook');
  const location = useLocation();
  const stateFrom = typeof location.state?.from === 'string' ? location.state.from : null;
  const stateListFrom = typeof location.state?.listFrom === 'string' ? location.state.listFrom : null;
  const backTo = stateFrom || `/${segment}`;

  /**
   * The breadcrumb's list crumb (issue 197): the ORIGINATING list URL, filters
   * and all, so the crumb restores the exact results the visit started from.
   * Arriving from this Family's own list, `state.from` IS that URL — the same
   * value the back link uses, one notion of provenance, not two. Arriving from
   * ANOTHER detail (a reference link), `state.from` is that detail's URL, so
   * the origin rides one field over: every reference link threads `listFrom`
   * through, which keeps the filtered list reachable at any depth. A deep link
   * carries neither and the crumb degrades to the bare list route — the issue's
   * own answer to its open question. Scroll restore comes free: `useListNav`'s
   * cache was written on the original list click and is only consumed when the
   * list remounts.
   */
  const isOwnListUrl = (url) => {
    if (!url) return false;
    const [path, search = ''] = url.split('?');
    return path === `/${segment}` && !new URLSearchParams(search).has('slug');
  };
  const listCrumbTo = isOwnListUrl(stateFrom) ? stateFrom
    : (isOwnListUrl(stateListFrom) ? stateListFrom : `/${segment}`);

  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bumped by "Try again" on a failed load: the fetch below runs once more.
  const [attempt, setAttempt] = useState(0);

  // Reader / focus mode (#196). The hook owns the `reader-mode` class on
  // <html> for exactly as long as this page is mounted and not in its error
  // state — the 404 card renders with full chrome, and unmounting (a list, an
  // authoring screen) restores the chrome while the stored preference stays.
  // State lives in the hook + localStorage, never the URL: it is a reading
  // preference, not part of the document address.
  const [readerMode, toggleReaderMode] = useReaderMode(!!error);

  const { singular, plural } = family.labels;
  const familyKey = family.family;

  // Condition terms (issue 36, phase 4b). `entry.conditions` is the reference
  // phase 4a populated, and because 4a only ever linked Tier A terms it is
  // already the filtered list the annotator needs — no term list lives in the
  // client, so the two cannot drift. The fetch is only for the hover card's
  // TEXT; the links render without it, and it is cached once per session.
  const conditionTerms = React.useMemo(
    () => (Array.isArray(entry && entry.conditions) ? entry.conditions : [])
      .filter((c) => c && c.slug && c.name),
    [entry],
  );
  const [, setConditionText] = useState(0);
  useEffect(() => {
    if (!conditionTerms.length) return undefined;
    let alive = true;
    loadConditions(getActiveSystem()).then(() => { if (alive) setConditionText((n) => n + 1); });
    return () => { alive = false; };
  }, [conditionTerms.length]);

  useEffect(() => {
    let cancelled = false;
    const fetchEntry = async () => {
      try {
        setLoading(true);
        const query = rulebook ? `?rulebook=${encodeURIComponent(rulebook)}` : '';
        const response = await apiClient.get(
          `/api/${segment}/${encodeURIComponent(slug)}${query}`
        );
        if (cancelled) return;
        setEntry(response.data);
        setError(null);
        track('detail-open', { family: familyKey });
        ensureSystemInUrl();
        setLoading(false);
      } catch (err) {
        if (!cancelled && err.response?.status === 404
            && await attemptCrossSystemRedirect(segment, slug)) {
          return; // redirecting to the owning system — keep the spinner up
        }
        if (cancelled) return;
        // Only a 404 is "not found": a server that failed or did not answer is
        // said as that, with a retry, because the entry may well exist.
        setError(err.response?.status === 404
          ? { text: `${singular} not found`, retry: false }
          : { text: loadFailureText(err, `This ${singular.toLowerCase()}`), retry: true });
        console.error(`Error fetching ${segment} entry:`, err);
        setLoading(false);
      }
    };
    fetchEntry();
    return () => { cancelled = true; };
  }, [segment, slug, rulebook, singular, attempt, familyKey]);

  // A DM's house version (qa-runs#366): opened from a character's sheet (`?character=`), the page asks whether that
  // character's campaign plays its own version of this entry, and shows its name and text above the book's.
  const characterId = searchParams.get('character');
  const [house, setHouse] = useState(null);
  useEffect(() => {
    setHouse(null);
    if (!characterId || !/^[0-9a-f-]{36}$/i.test(characterId) || !slug || !familyKey) return undefined;
    let alive = true;
    apiClient.get(`/api/characters/${characterId}/house-entry`, { params: { address: `${familyKey}/${slug}` } })
      .then((r) => { if (alive && r.data && r.data.house) setHouse(r.data.house); })
      .catch(() => {});
    return () => { alive = false; };
  }, [characterId, slug, familyKey]);

  // Recently-viewed history (issue 157): fire-and-forget, signed-in users
  // only, and only on a SUCCESSFUL load — a 404 or a cross-system redirect is
  // not a view. Gated on the same authored `favoritable` flag as the heart,
  // which is also what the server accepts (favorites' vocabulary for "content
  // a person can reference by address"). `entry.slug`, never the URL's slug,
  // for SimilarRail's reason: a visit that arrived via the name-slugification
  // second chance renders fine, but the server validates the recorded address
  // against the CANONICAL slug, so posting the alias silently recorded
  // nothing (and the favorite heart on the same page failed the same way).
  useRecordView(segment, entry?.slug || slug,
    !loading && !error && !!entry && !!family.favoritable);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner h-10 w-10" role="status" aria-label="Loading"></div>
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="card text-center" role={error && error.retry ? 'alert' : undefined}>
        <p className="error-message">{(error && error.text) || `${singular} not found`}</p>
        <div className="flex flex-wrap justify-center gap-2 mt-4">
          {error && error.retry && (
            <button type="button" className="btn btn-primary" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
          )}
          <Link to={backTo} className={`btn ${error && error.retry ? 'btn-secondary' : 'btn-primary'}`}>Back to {plural}</Link>
        </div>
      </div>
    );
  }

  const from = `${location.pathname}${location.search}`;
  const name = entry[family.nameAttr.key] || entry.name;
  const variant = family.detail.style;
  const subtitleSection = family.detail.sections.find((s) => s.placement === 'subtitle');
  const subtitle = subtitleSection
    ? composeLine((subtitleSection.config || {}).pattern, entry)
    : '';

  const headerBadges = family.detail.headerBadges
    .flatMap((attr) => badgeItems(attr, entry[attr.key]));

  const printings = entry.printings || [];
  const resolved = resolvedPrinting(printings, rulebook);
  // The picker writes the pin into the URL, which is what the fetch effect
  // already reads — one mechanism for the button and the shared link.
  const pinPrinting = (slug) => {
    const next = new URLSearchParams(searchParams);
    next.set('rulebook', slug);
    setSearchParams(next);
  };

  // Prose renders outside the stat block, as its own card — which is what every
  // shipped stat-block page does with a description. R12 widens that to every
  // style that brings its own container (`CARD_ONLY_STYLES`).
  const inBlock = family.detail.sections.filter((s) => !CARD_ONLY_STYLES.has(s.style));
  const afterBlock = family.detail.sections.filter((s) => CARD_ONLY_STYLES.has(s.style));
  const sections = variant === 'stat-block'
    ? { inBlock, afterBlock }
    : { inBlock: [], afterBlock: family.detail.sections };

  // A heading of the system's stat-block layout (issue 1591) holds sections of
  // its own, drawn the same way inside it.
  const renderSections = (list) => list.map((section, i) => (section.kind === 'heading' ? (
    <StatHeading key={`heading-${section.key}`} section={section} values={entry} variant={variant}>
      {renderSections(section.sections)}
    </StatHeading>
  ) : (
    <ConditionTermsProvider
      key={section.group || section.attr?.key || `rows-${i}`}
      value={conditionTerms}
    >
      <DetailSection
        section={section}
        values={entry}
        from={from}
        listFrom={listCrumbTo}
        variant={variant}
      />
    </ConditionTermsProvider>
  )));

  return (
    <>
      <Helmet>
        <title>{name} - The Dragon&apos;s Table</title>
        <meta name="description" content={`${name} — ${singular.toLowerCase()} details.`} />
      </Helmet>

      <div className={variant === 'stat-block' ? 'space-y-6 max-w-3xl mx-auto' : 'space-y-6'}>
        <Breadcrumbs listLabel={plural} listTo={listCrumbTo} current={name} />

        <Link to={backTo} className="back-link">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to {plural}</span>
        </Link>

        {house && (
          <section className="card p-4 space-y-2" data-testid="house-version">
            <h2 className="text-lg font-semibold">{house.name} (house rule)</h2>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Your campaign plays its own version of this {singular.toLowerCase()}; the book&apos;s text follows.</p>
            {house.text && <p className="whitespace-pre-line">{house.text}</p>}
          </section>
        )}

        {variant === 'stat-block' ? (
          <div className="stat-block">
            <div className="stat-block-header">
              <div className="flex items-start justify-between gap-4">
                {/* flex-wrap: the action row is six controls now (heart, two
                    copy, print, reader) — without it the cluster overflows
                    UNDER the flex-shrink-0 header badge at 360px, which then
                    intercepts every click on the overflowed buttons. */}
                <div className="flex flex-wrap items-start gap-1 min-w-0">
                  <h1 className="stat-block-name">{name}</h1>
                  {/* entry.slug, not the URL's: an alias-resolved page must favorite
                    the canonical address or the POST 404s (see useRecordView). */}
                {family.favoritable && <FavoriteButton entityType={segment} slug={entry.slug} />}
                  <CopyEntryButtons family={family} entry={entry} />
                  <WorkbenchLink segment={segment} slug={entry.slug} />
                  <PrintButton />
                  <ReaderModeButton active={readerMode} onToggle={toggleReaderMode} />
                </div>
                {headerBadges.map((badge) => (
                  <span
                    key={badge.key}
                    // Both shipped classes: `SpellDetail` styled its header
                    // badge `spell-school-badge` and `FeatDetail` styled its
                    // `badge`, and the generic header has one cluster. The
                    // second is a strict superset of the first's box styling,
                    // so carrying both reproduces either page and keeps the
                    // tighter school-badge padding.
                    className={`badge spell-school-badge flex-shrink-0 mt-1 ${badge.token}`}
                  >
                    {badge.text}
                  </span>
                ))}
              </div>
              {subtitle && <p className="stat-block-subtitle">{subtitle}</p>}
            </div>

            <ErrorBoundary>
              <div className="stat-block-body">
                <PrintingsPicker
                  printings={printings}
                  resolved={resolved}
                  onPin={pinPrinting}
                />
                {renderSections(sections.inBlock)}
                {entry.rulebook_name && (
                  <>
                    <hr className="stat-block-divider" />
                    <div className="stat-block-row">
                      <BookOpen
                        className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                        style={{ color: 'var(--text-muted)' }}
                        aria-hidden="true"
                      />
                      <span className="stat-block-value text-muted">
                        <RulebookLink slug={entry.rulebook_slug} name={entry.rulebook_name} from={from} listFrom={listCrumbTo} />
                      </span>
                    </div>
                  </>
                )}
              </div>
            </ErrorBoundary>
          </div>
        ) : (
          <div className="card">
            <div className="flex flex-wrap items-start gap-1 min-w-0">
              <h1 className="page-title">{name}</h1>
              {/* entry.slug, not the URL's: an alias-resolved page must favorite
                    the canonical address or the POST 404s (see useRecordView). */}
                {family.favoritable && <FavoriteButton entityType={segment} slug={entry.slug} />}
              <CopyEntryButtons family={family} entry={entry} />
              <WorkbenchLink segment={segment} slug={entry.slug} />
              <PrintButton />
              <ReaderModeButton active={readerMode} onToggle={toggleReaderMode} />
            </div>
            {/* The header cluster is not a stat-block idea: the shipped Skills
                and Deities details — both `cards` families — put their badge and
                their source book directly under the name, in the same card. It
                is the same authored `detail_slot: "header"` claim, rendered in
                the other visual language. */}
            {(headerBadges.length > 0 || entry.rulebook_name) && (
              <div className="mt-2 page-header">
                {headerBadges.map((badge) => (
                  <span key={badge.key} className={`badge ${badge.token}`}>{badge.text}</span>
                ))}
                {entry.rulebook_name && (
                  <div className="detail-rulebook">
                    <BookOpen className="w-4 h-4" aria-hidden="true" />
                    <span>
                      <RulebookLink slug={entry.rulebook_slug} name={entry.rulebook_name} from={from} listFrom={listCrumbTo} />
                    </span>
                  </div>
                )}
              </div>
            )}
            {/* The picker is a platform section and belongs to both visual
                languages: a `cards` Family's printings are no less pickable
                than a stat block's. It is the same component and the same
                markup — `.stat-block-section` is the heading style both
                engines already share. */}
            <PrintingsPicker
              printings={printings}
              resolved={resolved}
              onPin={pinPrinting}
            />
          </div>
        )}

        {/* The platform templates panel (05 §2.2). It is DATA-GATED — an
            Entity-Pillar family in a system that declares Template-Pillar
            content — which replaces `MonsterDetail.js:413`'s
            `system === 'dnd-3.5'`, the app's only per-system detail
            conditional. The panel component itself is unchanged: it reads the
            entry's ability scores and CR, which the generic detail payload
            carries under the same keys. */}
        {family.detail.templatesPanel && <MonsterTemplates monster={entry} />}

        <ErrorBoundary>
          <div className="space-y-6">
            <IncompleteNotice resolved={resolved} labelPlural={singular.toLowerCase()} />
            {renderSections(sections.afterBlock)}
            {/* The roll panel (issue #311): page content like the rail below.
                Gated on the registry — the Family declares an attribute tagged
                with the `outcomes` register (its `show_in_detail` is off, so
                this panel is that attribute's one renderer) — which also
                covers a user-authored Family served through the runtime
                registry. `entry.slug` for the SimilarRail reason below. */}
            {(() => {
              const outcomesAttr = family.attributes.find((a) => a.register === 'outcomes');
              return outcomesAttr ? (
                <RollTablePanel
                  segment={segment}
                  slug={entry.slug}
                  outcomes={entry[outcomesAttr.key]}
                  from={from}
                  listFrom={listCrumbTo}
                />
              ) : null;
            })()}
            <AlsoAppearsIn printings={printings} resolved={resolved} from={from} listFrom={listCrumbTo} />
            {/* "Similar to this" (issue 189): page content, not an action-row
                control (#287). `entry.slug` rather than the URL's slug, so a
                visit that arrived via the name-slugification second chance
                asks about the entry actually rendered. */}
            <SimilarRail
              segment={segment}
              slug={entry.slug}
              from={from}
              listFrom={listCrumbTo}
              labelPlural={plural}
            />
            {/* "To qualify" (issue #201): the flattened prerequisite closure,
                page content like the rail above it. The server answers
                { qualify: null } for a Family with no closure edge or a
                closure the page's own prerequisites group already states, and
                the panel renders nothing. `entry.slug` for the SimilarRail
                reason above. */}
            <QualifyPanel
              segment={segment}
              slug={entry.slug}
              from={from}
              listFrom={listCrumbTo}
            />
            {/* Personal note (issue 193): page content, not an action-row
                control (#287). Gated on the same authored `favoritable` flag
                as the heart and the server's own vocabulary for "content a
                person can reference by address"; guests render nothing.
                `entry.slug` for the SimilarRail reason above. */}
            {family.favoritable && (
              <EntryNotePanel entityType={segment} slug={entry.slug} />
            )}
          </div>
        </ErrorBoundary>
      </div>
    </>
  );
}
